import type { ProjectState } from "hooks/useProjects/useProjects.types";

export type AiCreditsWalletKind = NonNullable<ProjectState["aiCredits"]>["walletKind"];
export type AiCreditsLicenseTier = NonNullable<ProjectState["otpSeating"]>["licenseTier"];

export function getAiCreditsWalletSummary(walletKind: string): string {
  switch (walletKind) {
    case "workspace_subscription":
      return "Included with your active workspace plan (not limited by credit balance).";
    case "project_otp":
      return "Credits included with this project's license. Extra credit packs are not sold separately—upgrade the project license for a larger pool.";
    case "demo":
      return "Demo project allowance.";
    case "unlicensed_project":
      return "Limited allowance until this project is licensed.";
    default:
      return "AI credits for this project.";
  }
}

export type AiCreditsPurchaseCtaIntent = "credits-page" | "default";

export function getAiCreditsPurchaseCtaLabel(
  walletKind: string,
  intent: AiCreditsPurchaseCtaIntent = "default"
): string {
  // Checkout sells Writer/Studio licenses, not à-la-carte credit packs.
  void intent;
  switch (walletKind) {
    case "unlicensed_project":
      return "Upgrade project";
    case "project_otp":
      return "Upgrade project";
    case "demo":
      return "Upgrade from demo";
    default:
      return "Upgrade project";
  }
}

/** Primary license-type label for settings and detail views. */
export function getAiCreditsLicenseTypeLabel(
  walletKind: string,
  licenseTier?: AiCreditsLicenseTier
): string {
  switch (walletKind) {
    case "workspace_subscription":
      return "Workspace plan";
    case "project_otp":
      if (licenseTier === "studio") return "Studio license";
      if (licenseTier === "writer") return "Writer license";
      return "Project license";
    case "demo":
      return "Demo";
    case "unlicensed_project":
      return "Unlicensed";
    default:
      return "Project";
  }
}

/** Short badge label for compact UI (dropdowns, chips). */
export function getAiCreditsWalletBadge(walletKind: string): string {
  switch (walletKind) {
    case "workspace_subscription":
      return "Workspace plan";
    case "project_otp":
      return "Project pack";
    case "demo":
      return "Demo";
    case "unlicensed_project":
      return "Unlicensed";
    default:
      return "Project";
  }
}

export function formatAiCreditActionLabel(actionKey: string): string {
  const map: Record<string, string> = {
    scheduling_rerun: "AI schedule",
    budget_rerun: "AI budget",
    script_breakdown_rerun: "AI script breakdown",
    character_breakdown_rerun: "AI character breakdown",
    coverage_rerun: "AI coverage",
  };
  if (map[actionKey]) return map[actionKey];
  const words = actionKey
    .replace(/_rerun$/, "")
    .split(/[_\s]+/)
    .filter(Boolean);
  if (words.length === 0) return actionKey;
  return [
    words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase(),
    ...words.slice(1).map((w) => w.toLowerCase()),
  ].join(" ");
}

const AI_CREDIT_ACTION_DISPLAY_ORDER: Record<string, number> = {
  budget_rerun: 0,
  scheduling_rerun: 1,
  script_breakdown_rerun: 2,
  character_breakdown_rerun: 3,
  coverage_rerun: 4,
};

export function compareAiCreditActions(
  a: { key: string; label: string },
  b: { key: string; label: string }
): number {
  const aOrder = AI_CREDIT_ACTION_DISPLAY_ORDER[a.key] ?? Number.POSITIVE_INFINITY;
  const bOrder = AI_CREDIT_ACTION_DISPLAY_ORDER[b.key] ?? Number.POSITIVE_INFINITY;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return a.label.localeCompare(b.label);
}
